import { ChartTimeframe } from '@/types/generated/common/types'
import type { DataPayload, OrderbookHeatmap } from '@/types/generated/public_api'

export type DataPayloadArm = Exclude<keyof DataPayload, 'accountId'>

/**
 * Server-push payload arms and the listener keys they must reach. This contract
 * starts from the payload arm (not from a hook key), so two analytics arms that
 * share one listener key cannot hide an omitted arm from the test suite.
 */
export const STREAM_PAYLOAD_ROUTES = {
  tickers: ['marketDataStream:tickers'],
  incrementalTickers: ['marketDataStream:incrementalTickers'],
  chartData: ['chartDataStream'],
  orderBooks: ['marketDataStream:orderbook'],
  instrumentTrades: ['marketDataStream:trades', 'optionsTradesStream'],
  trades: ['accountStream'],
  positions: ['accountStream'],
  unifiedOrders: ['accountStream'],
  portfolioData: ['accountStream'],
  rfqUpdates: ['accountStream'],
  rfqAccept: ['accountStream'],
  rfqMakerRfqs: ['accountStream'],
  rfqMakerQuotes: ['accountStream'],
  accountFees: ['accountStream'],
  accountSummary: ['accountStream'],
  instruments: ['exchangeData'],
  notification: ['serviceNotifications'],
  exchangeStatus: ['exchangeStatusStream'],
  venueHealth: ['venueHealthStream'],
  marketScreenerFrame: ['marketScreenerStream'],
  // Live frames only: see isLiveOrderbookHeatmap.
  orderbookHeatmap: ['orderbookHeatmapStream'],
  economicCalendarEvent: ['economicCalendarStream'],
  tokenUnlockEvent: ['tokenUnlockStream'],
  triggerEvaluationSnapshot: ['triggerConditionStream'],
  dbNewsPost: ['dbNewsStream'],
  alpacaNewsPost: ['alpacaNewsStream'],
  twitterPost: ['twitterStream'],
  treeNewsPost: ['treeNewsStream'],
  coinmarketcalEvent: ['coinmarketcalEventStream'],
  executionAnalytics: ['executionAnalyticsStream'],
  optionsVolumeStreamUpdate: ['optionsVolumeStream'],
  volMetrics24h: ['analyticsStream'],
  putCallRatio: ['analyticsStream'],
  deltaSurfaceConstant: ['analyticsStream'],
  deltaSurfaceFloating: ['analyticsStream'],
  impliedVsRealized: ['analyticsStream'],
  termStructureConstant: ['analyticsStream'],
  termStructureFloating: ['analyticsStream'],
  termStructureRichness: ['analyticsStream'],
  volatilityIndex: ['analyticsStream'],
  fedwatchProbabilities: ['analyticsStream'],
  liquidationLevelProfile: ['analyticsStream'],
  stopLevelProfile: ['analyticsStream'],
} as const satisfies Partial<Record<DataPayloadArm, readonly string[]>>

/**
 * The `OrderbookHeatmapStream` push is the `orderbookHeatmap` arm with no timeframe: its single
 * column is the book now, drawn over whichever bar is open on any timeframe. The
 * `OrderbookHeatmapRequest` reply shares the arm but arrives over HTTP today; this keeps one sent
 * over the socket out of the stream. The `??` lets the per-arm contract test route a bare `{}`.
 */
export function isLiveOrderbookHeatmap(heatmap: OrderbookHeatmap): boolean {
  return (
    (heatmap.timeframe ?? ChartTimeframe.CHART_TIMEFRAME_UNSPECIFIED) ===
    ChartTimeframe.CHART_TIMEFRAME_UNSPECIFIED
  )
}

/**
 * These shipped analytics intentionally stay REST-only. Removing an entry is a
 * product behavior change and must be paired with widget-level stream testing.
 */
export const INTENTIONALLY_UNROUTED_STREAM_PAYLOADS = [
  'usdGamma',
  'gex',
  'volCones',
  'skewConstant',
  'skewFloating',
  'rvSeasonalityDow',
  'rvSeasonalityMoy',
  'rvSeasonalityHour',
] as const satisfies readonly DataPayloadArm[]
